import { RefObject } from "react";
import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { LoadIndividualResponse } from "@/Common/Api/Individuals/Dto/LoadIndividualResponse";
import { SearchIndividualResponse } from "@/Common/Api/Individuals/Dto/SearchIndividualResponse";
import { SearchIndividualResult } from "@/Common/Api/Individuals/Dto/SearchIndividualResult";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { LoadingIndicator } from "@/Common/Components/Loader/LoadingIndicator";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo, RamqStatus } from "@/Common/Models/RAMQ/RAMQInfo";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IndividualAdvancedSearchResult } from "./IndividualAdvancedSearchResult";

import img_patient_icon from "@/Common/_images/patient-icon.png";
import img_relative_icon from "@/Common/_images/relative-icon.png";
import img_loading from "@/Globals/_images/loading.gif";

interface IIndividualSelectCallbackFunction {
    (selectedperson: Person, text?: string): void;
}

interface ICustomSearchResultRenderFunction {
    (person: Person, individualContainerClassName: string, idx: number, selectFunction: (p: Person) => void): JSX.Element;
}

interface IIndividualAdvancedSearchControlProps {
    componentId: string;
    cssClass?: string;
    customMyleIndividualRenderFunction?: ICustomSearchResultRenderFunction; // use these to override default rendering of search results
    customPortalPatientRenderFunction?: ICustomSearchResultRenderFunction; // use these to override default rendering of search results
    defaultTextValue?: string;
    hideIndividualImage?: boolean;
    hvar?: string;
    isAutoResize?: boolean;
    isClickOnResultDisabled?: boolean;
    isFocusOnLoad?: boolean;
    isIncludingInactiveIndividuals?: boolean;
    isPortalSearchIncludeAlreadyLinked?: boolean;
    isRenderTextbox?: boolean;
    isScrollBarActive?: boolean;
    isSearchingPortal?: boolean;
    isSearchingRelatives?: boolean;
    isSearchingPatients?: boolean;
    key?: string;
    maxResultsToReturn: number
    onIndividualSelectedOperation?: number;
    onIndividualSearchResultAmountCallback?: (amount: number) => void;
    onIndividualSelectCallback?: IIndividualSelectCallbackFunction;
    resultCSS?: React.CSSProperties;
    individualsToOmitInSearchResults?: string[];
    specificContactIdsToDisplay?: string[]; // when we want the results to only display specific individuals (e.g. for displaying duplicate file)
    currentPersonContext?: Person;
    ignorePatientsAlreadyPartOfMergeRequest?: boolean;
    dataCy?: string;
}

class IndividualAdvancedSearchState {
    public forceRenderId: number = 0; // A number incremented to force the control to re-render when asynchronous data changes.
    public searchTextValue: string;
    public searchPersonResults: Person[];
    public selectedPerson: Person;

    public static getDefault(props: IIndividualAdvancedSearchControlProps): IndividualAdvancedSearchState {
        const s = new IndividualAdvancedSearchState();
        s.searchTextValue = props.defaultTextValue ?? "";
        s.searchPersonResults = [];
        return s;
    }
}

class IndividualAdvancedSearchControl extends BaseReactComponent<IIndividualAdvancedSearchControlProps, IndividualAdvancedSearchState> {
    private static readonly cacheTimeoutSeconds = 90;

    private validationHelper: ValidationHelper = new ValidationHelper();
    private MylePersonCacheDictionnary: { [contactId: string]: IndividualSearchResultCachedPromise<Person> } = {};
    private textInputRef: RefObject<HTMLInputElement> = React.createRef();
    private timeoutReference: number | undefined;
    private orderedResponseId: number = 0;
    private timeoutReferenceUIRefresh: number = -1;
    private patientRenderColumn2: React.CSSProperties = { justifyContent: "flex-start", width: "100%" };
    private patientRenderColumn2Div1: React.CSSProperties = { width: "50%" };
    private patientRenderColumn2Div2: React.CSSProperties = { width: "50%" };

    constructor(props: IIndividualAdvancedSearchControlProps) {
        super(props);

        this.state = IndividualAdvancedSearchState.getDefault(props);
    }

    public componentDidUpdate(prevProps: Readonly<IIndividualAdvancedSearchControlProps>): void {
        const defaultValueChanged = prevProps.defaultTextValue !== this.props.defaultTextValue;
        const shouldResetList =
        (
            prevProps.isIncludingInactiveIndividuals !== this.props.isIncludingInactiveIndividuals
            || prevProps.isSearchingPortal !== this.props.isSearchingPortal
            || prevProps.isSearchingPatients !== this.props.isSearchingPatients
            || prevProps.isSearchingRelatives !== this.props.isSearchingRelatives
        );

        if (!defaultValueChanged && !shouldResetList)
            return;

        this.mutateState(
            s => {
                if (defaultValueChanged)
                    s.searchTextValue = this.props.defaultTextValue;
            },
            () => this.invokeDebouncedSearch(this.state.searchTextValue)
        );
    }

    public componentDidMount(): void {
        if (this.props.isFocusOnLoad)
            this.textInputRef.current?.focus();

        if (!IsNullOrWhiteSpace(this.props.defaultTextValue) || this.props.specificContactIdsToDisplay?.any())
            this.invokeDebouncedSearch(this.props.defaultTextValue);

        this.adjustWindowScrollbar();
    }

    public adjustWindowScrollbar(): void {
        if (!this.props.isAutoResize)
            return;
        window.setTimeout(() => $(window).trigger("resize"), 150);
    }

    public shouldComponentUpdate(nextProps: IIndividualAdvancedSearchControlProps, nextState: IndividualAdvancedSearchState): boolean {
        const changed =
            this.props.defaultTextValue !== nextProps.defaultTextValue
            || this.props.isIncludingInactiveIndividuals !== nextProps.isIncludingInactiveIndividuals
            || this.props.isSearchingPatients !== nextProps.isSearchingPatients
            || this.props.isSearchingRelatives !== nextProps.isSearchingRelatives
            || this.props.isSearchingPortal !== nextProps.isSearchingPortal
            || this.state.searchPersonResults !== nextState.searchPersonResults
            || this.state.searchTextValue !== nextState.searchTextValue
            || this.state.selectedPerson !== nextState.selectedPerson
            || this.state.forceRenderId !== nextState.forceRenderId;

        return changed;
    }

    public componentWillUnmount(): void {
        this.adjustWindowScrollbar();
    }

    public render(): JSX.Element {
        const css = this.props.cssClass == null ? "" : this.props.cssClass + " ";
        const wrapperCss = this.state.searchPersonResults.length > 0 ? css + "pa-selected-items-list" : css + "pa-selected-items-list empty";

        let scrollbarCss = "";
        if (this.props.isScrollBarActive) {
            scrollbarCss = "patient-sidebar-search-container fxheight";
        }

        let disabledSearchClass = "";
        if (this.props.isClickOnResultDisabled) {
            disabledSearchClass = " disabled-results";
        }

        let hvar = this.props.hvar == null ? "-52" : this.props.hvar;

        let resultCSS: React.CSSProperties = this.props.resultCSS ?? {};

        return (
            <div id={this.props.componentId} onClick={event => this.stopPropagation(event)} className={disabledSearchClass}>
                {this.props.isRenderTextbox && (
                    <div>
                        <input onChange={event => this.searchTextChanged(event)} value={this.state.searchTextValue} type="text" className="form-control" ref={this.textInputRef} data-cy={this.props.dataCy ? `${this.props.dataCy}-input` : null} />
                    </div>
                )}

                <div data-hvar={hvar} className={wrapperCss + " " + scrollbarCss} style={resultCSS} data-cy={this.props.dataCy ? `${this.props.dataCy}-div` : null}>
                    {this.state.searchPersonResults.map((person: Person, idx: number) => {
                        const isSelected =
                            this.state.selectedPerson
                            && (
                                person === this.state.selectedPerson
                                || (person.ContactId && person.ContactId === this.state.selectedPerson.ContactId)
                                || (!person.ContactId && person.PortalProfileId && person.PortalProfileId === this.state.selectedPerson.PortalProfileId)
                            );
                        const patientDivClassName = isSelected
                            ? "flex-container selectable selected"
                            : "flex-container selectable notselected";

                        if (!this.props.isSearchingPortal && this.MylePersonCacheDictionnary[person.ContactId]?.loadedData) {
                            if (this.props.customMyleIndividualRenderFunction != null) {
                                return this.props.customMyleIndividualRenderFunction(person, patientDivClassName, idx, p => this.onIndividualSelect(p));
                            }
                            else {
                                return (
                                    <IndividualAdvancedSearchResult
                                        person={person}
                                        currentPersonContext={this.props.currentPersonContext}
                                        rootClassName={patientDivClassName}
                                        hideIndividualImage={this.props.hideIndividualImage}
                                        onClick={() => this.onIndividualSelect(person)}
                                        key={person.ContactId}
                                    />
                                );
                            }
                        }
                        else if (this.props.isSearchingPortal && person.PortalPatient != null) {
                            return this.renderWithPortalInfo(person, patientDivClassName, idx);
                        }
                        else {
                            return this.renderWithPartialInfo(person, patientDivClassName, idx);
                        }
                    })}
                </div>

            </div>
        );
    }

    private renderWithPartialInfo(person: Person, rootClassName: string, idx: number): JSX.Element {
        return (
            <div className={rootClassName} onClick={() => this.onIndividualSelect(person)} key={person.ContactId + "_" + idx}>
                {this.props.hideIndividualImage
                    ? (
                        <div style={{ width: "5px" }} />
                    ) : (
                        <div className="flex-col r1">
                            <img title={person.NameWithIdentifier} src={this.GetPersonIcon(person)} style={{ width: "48px", paddingTop: "5px" }} alt={person.NameWithIdentifier} />
                        </div>
                    )
                }
                <div className="flex-container" style={this.patientRenderColumn2}>
                    <div className="flex-col" style={this.patientRenderColumn2Div1}>
                        <h4 className="light-blue margin-none-top">{person.NameWithIdentifier}<span className="myle-icon-16 female" /></h4>
                        <ul>
                            <li><strong>{cacheStore.GetString("FileNumbersLabel")}</strong></li>
                            <li>...</li>
                            <li>...</li>
                        </ul>
                    </div>
                    <div className="flex-col" style={this.patientRenderColumn2Div2}>
                        <ul>
                            <li><LoadingIndicator imagePath={img_loading} /></li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    private renderWithPortalInfo(person: Person, rootClassName: string, idx: number): JSX.Element {
        let address1 = "";
        let dateOfBirth = "";
        let formattedAge = "";
        let phone1 = "";
        let phone2 = "";
        let phone3 = "";
        let email = "";

        if (person.PortalPatient.TelephoneNumbers != null && person.PortalPatient.TelephoneNumbers.length >= 1) {
            let formatted = PortalHelper.getFormattedPhoneNumber(person.PortalPatient.TelephoneNumbers[0], this.validationHelper);
            phone1 = formatted.FormattedPhoneNumber;
        }
        if (person.PortalPatient.TelephoneNumbers != null && person.PortalPatient.TelephoneNumbers.length >= 2) {
            let formatted = PortalHelper.getFormattedPhoneNumber(person.PortalPatient.TelephoneNumbers[1], this.validationHelper);
            phone2 = formatted.FormattedPhoneNumber;
        }
        if (person.PortalPatient.TelephoneNumbers != null && person.PortalPatient.TelephoneNumbers.length >= 3) {
            let formatted = PortalHelper.getFormattedPhoneNumber(person.PortalPatient.TelephoneNumbers[2], this.validationHelper);
            phone3 = formatted.FormattedPhoneNumber;
        }
        if (person.PortalPatient.MailingAddresses != null && person.PortalPatient.MailingAddresses.length >= 1) {
            let formatted = PortalHelper.getFormattedAddress(person.PortalPatient.MailingAddresses[0], this.validationHelper, true);
            address1 = formatted;
        }

        if (person.PortalPatient.DateOfBirth != null && person.PortalPatient.DateOfBirth.length > 0) {
            let birthdate = moment(person.PortalPatient.DateOfBirth);
            dateOfBirth = birthdate.format("YYYY-MM-DD");
            formattedAge = PatientHelper.getFormattedAge(birthdate.toDate());
        }

        if (person.PortalPatient.EmailAddresses != null && person.PortalPatient.EmailAddresses.length > 0) {
            email = person.PortalPatient.EmailAddresses[0].Email;
        }

        let portalButton: JSX.Element = null;

        if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
            const buttonProps = PortalHelper.getPortalButtonPropsForRegistrationState(person.PortalSyncState);
            portalButton = buttonProps &&
                <div className="margin-xs-top"><PortalStatusIcon {...buttonProps} /></div>;
        }

        return (
            <div className={rootClassName} onClick={() => this.onIndividualSelect(person)} key={person.ContactId + "_" + idx}>
                {this.props.hideIndividualImage
                    ? (
                        <div style={{ width: "5px" }} />
                    ) : (
                        <div className="flex-col r1">
                            <img title={person.NameWithIdentifier} src={img_patient_icon} style={{ width: "48px", paddingTop: "5px" }} alt={person.NameWithIdentifier} />
                            {portalButton}
                        </div>
                    )
                }
                <div className="flex-container" style={this.patientRenderColumn2}>
                    <div className="flex-col" style={this.patientRenderColumn2Div1}>
                        <h4 className="light-blue margin-none-top">{person.Name}<span className="myle-icon-16 female" /></h4>
                        <ul>
                            <li><strong>{sessionStore.getDefaultHealthCardLabel()} </strong><span>{person.GetFormattedNAM()}</span></li>
                            <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span>{dateOfBirth + " (" + formattedAge + ") "}</span></li>
                        </ul>
                    </div>

                    <div className="flex-col" style={this.patientRenderColumn2Div2}>
                        <ul>
                            {phone1 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone1 + " " + this.toSafeString(phone1[1]) + " " + this.toSafeString(phone1[2])}</span></li>}
                            {phone2 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone2 + " " + this.toSafeString(phone2[1]) + " " + this.toSafeString(phone2[2])}</span></li>}
                            {phone3 != "" && <li><span className="myle-icon-16 phone" /><span className="content-text">{phone3 + " " + this.toSafeString(phone3[1]) + " " + this.toSafeString(phone3[2])}</span></li>}
                            {address1 != "" && <li><span className="myle-icon-16 pin" /><span className="content-text">{address1}</span></li>}
                            {email != "" && <li title={email} style={{ maxWidth: "225px", overflow: "hidden", textOverflow: "ellipsis" }} className="content-text"><span style={{ overflow: "hidden", whiteSpace: "nowrap" }}> @ {email}</span></li>}
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    private onIndividualSelect(selectedPerson: Person): void {
        if ((this.props.onIndividualSelectedOperation || this.props.onIndividualSelectCallback) && selectedPerson.isLightModel)
            return;

        this.mutateState(
            s => s.selectedPerson = selectedPerson,
            () => {
                if (this.props.onIndividualSelectedOperation != null) {
                    sessionStore.dispatch(
                        this.props.onIndividualSelectedOperation,
                        { "person": selectedPerson }
                    );
                }

                this.props.onIndividualSelectCallback?.(selectedPerson, this.state.searchTextValue);
            }
        );
    }

    private stopPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private toSafeString(str: string): string {
        return str ?? "";
    }

    private searchTextChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        let newText = e.target.value;
        this.mutateState(s => s.searchTextValue = newText);
        this.invokeDebouncedSearch(newText);
    }

    private invokeDebouncedSearch(searchText: string): void {
        clearTimeout(this.timeoutReference);

        if (!searchText) {
            this.setState({ searchPersonResults: [] });
            return;
        }

        this.timeoutReference = window.setTimeout(
            () => {
                if (this.props.isSearchingPortal)
                    this.invokePortalPatientSearchDebounced(searchText);
                else
                    this.invokeMyleSearchDebounced(searchText);
            },
            350
        );
    }

    private async invokePortalPatientSearchDebounced(searchText: string): Promise<void> {
        const responseId = this.orderedResponseId + 1;
        this.orderedResponseId = responseId;

        let response = await Client.postAsync<any>(
            ServerOperations.Server.Portal_Proxy_SearchPatientProfiles,
            {
                IncludeLinked: this.props.isPortalSearchIncludeAlreadyLinked,
                Query: searchText
            }
        );

        if (!response.isSuccess)
            return;

        if (this.orderedResponseId === responseId)
            this.mutateState(s => s.searchPersonResults = PortalHelper.convertPortalPatientsToMylePersons(response.data.Clients));
    }

    private async invokeMyleSearchDebounced(searchText: string): Promise<void> {
        const responseId = this.orderedResponseId + 1;
        this.orderedResponseId = responseId;

        let mustSearchSpecificContactIds = this.props.specificContactIdsToDisplay?.length > 0;
        let selectFirstResult = mustSearchSpecificContactIds;

        if (!mustSearchSpecificContactIds && (searchText == null || searchText.length < 2))
            return;

        let response = await Client.postAsync<SearchIndividualResponse>(
            ServerOperations.Server.Myle_SearchIndividual,
            {
                Query: searchText,
                IsInactive: this.props.isIncludingInactiveIndividuals,
                IncludePatients: this.props.isSearchingPatients ?? true,
                IncludeRelatives: this.props.isSearchingRelatives ?? true,
                ResultsToReturn: this.props.maxResultsToReturn,
                QueryByPatientContactId: this.props.specificContactIdsToDisplay,
                IgnorePatientsAlreadyPartOfMergeRequest: this.props.ignorePatientsAlreadyPartOfMergeRequest,
                PatientContactIdToIgnore: this.props.ignorePatientsAlreadyPartOfMergeRequest ? this.props.currentPersonContext.ContactId : null
            }
        );

        if (!response.isSuccess || this.orderedResponseId !== responseId)
            return;

        const searchIndividuals: SearchIndividualResponse = response.data;

        if (this.props.individualsToOmitInSearchResults) {
            searchIndividuals.SearchIndividualResults = searchIndividuals.SearchIndividualResults
                .filter(result => !this.props.individualsToOmitInSearchResults.contains(result.ContactId));
        }

        this.props.onIndividualSearchResultAmountCallback?.(searchIndividuals.SearchIndividualResults.length);

        this.mutateState(
            state => {
                state.searchPersonResults = searchIndividuals.SearchIndividualResults
                    .map(result => {
                        const fullPatientPromise = this.getMylePersonPromisesFromCache(result.ContactId);
                        if (fullPatientPromise?.loadedData)
                            return fullPatientPromise.loadedData;

                        return Person.FromSearchIndividualResult(result);
                    });

                if (selectFirstResult && state.searchPersonResults?.length > 0)
                    state.selectedPerson = state.searchPersonResults[0];

                // prepare search jobs
                for (let result of searchIndividuals.SearchIndividualResults) {
                    const cachedPerson = this.getMylePersonPromisesFromCache(result.ContactId);
                    if (cachedPerson != null)
                        continue;

                    this.addMylePersonToCache(result.ContactId, this.loadMyleDetails(result));
                }
            },
            () => this.resumeMylePersonLoadJob(searchIndividuals.OrderedResponseId)
        );
    }

    private loadMyleDetails(searchResult: SearchIndividualResult): IndividualSearchResultCachedPromise<Person> {
        let cachedPromise = new IndividualSearchResultCachedPromise<Person>();

        cachedPromise.post = async () => {
            let person: Person = null;

            switch (searchResult.ContactFacetType) {
                case ContactFacetType.Patient:
                    person = await this.loadMylePatient(searchResult.ContactId);
                    break;
                default:
                    person = await this.loadMyleIndividual(searchResult.ContactId);
            }

            // when some promise resolve
            this.updateStateWithMyleInfo();

            return person;
        };

        return cachedPromise;
    }

    private async loadMyleIndividual(contactId: string): Promise<Person> {
        const response = await Client.postAsync<LoadIndividualResponse>(
            ServerOperations.Server.Myle_LoadIndividual,
            {
                IndividualId: contactId,
                IncludeRelatedRecalls: false
            }
        );

        if (!response.isSuccess)
            return null;

        return Person.fromLoadIndividual(response.data);
    }

    private async loadMylePatient(contactId: string): Promise<Person> {
        const response = await Client.postAsync<LoadPatientModel>(
            ServerOperations.Server.Myle_LoadPatient,
            {
                IndividualId: contactId,
                IncludeRelatedRecalls: false
            }
        );

        if (!response.isSuccess)
            return null;

        const patient = Patient.FromResponseData(response.data);

        if (sessionStore.clientConfiguration.HasMedfarRAMQ || cacheStore.ClinicSettings.EnableRamqGL6Service)
            await this.loadPatientRamqInfo(patient);
        else {
            const ramqInfo = new RAMQInfo();
            ramqInfo.Result = RamqStatus.Undefined;
            patient.RAMQInfo = ramqInfo;
        }

        return Person.FromPatient(patient);
    }

    private updateStateWithMyleInfo(): void {
        // Throttle
        clearTimeout(this.timeoutReferenceUIRefresh);

        this.timeoutReferenceUIRefresh = window.setTimeout(
            () => {
                // We have it in cache. Replace it
                this.mutateState(s =>
                    s.searchPersonResults = s.searchPersonResults.map(p => this.MylePersonCacheDictionnary[p.ContactId]?.loadedData ?? p)
                );
            },
            300
        );
    }

    private addMylePersonToCache(contactId: string, promise: IndividualSearchResultCachedPromise<Person>): void {
        this.MylePersonCacheDictionnary[contactId] = promise;

        // set timeout callback to auto delete cached item when cache maxtime is elapsed
        window.setTimeout(() => this.removeMylePersonFromCache(contactId), IndividualAdvancedSearchControl.cacheTimeoutSeconds * 1000);
    }

    private getMylePersonPromisesFromCache(personContactId: string): IndividualSearchResultCachedPromise<Person> {
        return this.MylePersonCacheDictionnary[personContactId];
    }

    private removeMylePersonFromCache(personContactId: string): void {
        delete this.MylePersonCacheDictionnary[personContactId];
    }

    private async loadPatientRamqInfo(patient: Patient): Promise<void> {
        const response = await Client.postAsync<RAMQInfo>(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patient.IndividualResponse.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );

        if (response.isSuccess) {
            const data = new RAMQInfo().FromResponseData(response.data);
            patient.setRamq(data);
        }
    }

    private async resumeMylePersonLoadJob(currentSearchId: number): Promise<void> {
        const patientsToLoad = this.state.searchPersonResults?.filter(p => p.isLightModel);
        if (patientsToLoad?.any()) {
            for (let patientToLoad of patientsToLoad) {
                const fullPatientPromise = this.MylePersonCacheDictionnary[patientToLoad.ContactId];
                await fullPatientPromise?.load();
                if (currentSearchId < this.orderedResponseId)
                    break;
            }
        }
    }

    private GetPersonIcon(person: Person): string {
        return person.ContactFacetType === ContactFacetType.Patient ? img_patient_icon : img_relative_icon;
    }
}

class IndividualSearchResultCachedPromise<T> {
    public post: () => Promise<T>;
    public loadedData: T;

    private currentLoad: Promise<T>;

    public async load(): Promise<T> {
        this.currentLoad ??= this.post();

        this.loadedData = await this.currentLoad;
        return this.loadedData;
    }
}

export { IIndividualSelectCallbackFunction, IndividualAdvancedSearchControl, IndividualSearchResultCachedPromise };
